import { ROUTES } from 'constants/routes';
import { IProject, WorkerInvitationStatus } from 'models/general';
import { WIZARD_STEPS, StepKey, getStepIndexFromPath } from 'modules/views/shared/Wizard/config';
import { isStepComplete, getFirstIncompleteStep } from 'modules/views/shared/Wizard/utils';
import { hasWorkerAcceptedTheProject, mustCreateWorkerPassword } from 'utils/onboarding';
import { CONSENT_ROUTE_SEQUENCE, getFirstIncompleteConsentRoute } from 'utils/consentSteps';
import { isResumePending } from 'utils/resumeProjectOnboarding';

export enum RouteCategory {
  SELECT_PROJECT = 'SELECT_PROJECT',
  LOGIN = 'LOGIN',
  FORGOT_RESET_PASSWORD = 'FORGOT_RESET_PASSWORD',
  WIZARD_REGISTRATION = 'WIZARD_REGISTRATION',
  WIZARD_CONSENT_REGISTRATION = 'WIZARD_CONSENT_REGISTRATION',
  WIZARD_STEP = 'WIZARD_STEP',
  CONSENT_FORM_INFO = 'CONSENT_FORM_INFO',
  WORKER_ONLY = 'WORKER_ONLY',
  ADMIN_ONLY = 'ADMIN_ONLY',
  AUTHENTICATED = 'AUTHENTICATED',
  OTHER = 'OTHER',
}

const WIZARD_STEP_PATHS = [
  ROUTES.TAKE_PHOTO.path,
  ROUTES.PERSONAL_INFORMATION.path,
  ROUTES.CONTACT_INFORMATION.path,
  ROUTES.TRADES.path,
  ROUTES.CERTIFICATIONS.path,
  ROUTES.CONSENT_FORM.path,
];

const WORKER_ONLY_PATHS = [ROUTES.PROJECT_LIST.path, ROUTES.MY_CERTIFICATIONS.path, ROUTES.MY_COURSES.path];

const AUTHENTICATED_PATHS = [ROUTES.MY_PROFILE.path];

export function classifyRoute(path: string): RouteCategory {
  const p = path.toLowerCase();

  if (p === ROUTES.SELECT_PROJECT.path) return RouteCategory.SELECT_PROJECT;
  if (p === ROUTES.LOGIN.path) return RouteCategory.LOGIN;
  if (p === ROUTES.FORGOT_PASSWORD.path || p === ROUTES.VERIFY_OTP.path || p === ROUTES.CONFIRM_RESET_PASSWORD.path) {
    return RouteCategory.FORGOT_RESET_PASSWORD;
  }

  if (p === ROUTES.BASIC_INFORMATION.path || p === ROUTES.REGISTRATION.path || p === ROUTES.CREATE_PASSWORD.path) {
    return RouteCategory.WIZARD_REGISTRATION;
  }

  if (
    p === ROUTES.TERMS_AND_CONDITIONS.path ||
    p === ROUTES.STANDARD_CONSENT_FORM.path ||
    p === ROUTES.STANDARD_BIOMETRIC_CONSENT.path ||
    p === ROUTES.REGISTRATION_SIGNATURE.path
  ) {
    return RouteCategory.WIZARD_CONSENT_REGISTRATION;
  }

  if (WIZARD_STEP_PATHS.includes(p)) return RouteCategory.WIZARD_STEP;
  if (p === ROUTES.CONSENT_FORM_INFORMATION.path) return RouteCategory.CONSENT_FORM_INFO;
  if (WORKER_ONLY_PATHS.includes(p)) return RouteCategory.WORKER_ONLY;
  if (p === ROUTES.WORKER_STATUS.path) return RouteCategory.ADMIN_ONLY;
  if (AUTHENTICATED_PATHS.includes(p)) return RouteCategory.AUTHENTICATED;

  return RouteCategory.OTHER;
}

interface RouteGuardInput {
  currentPath: string;
  user: {
    userId?: string | null;
    isWorker?: boolean;
    /** Set by `authenticateUser.fulfilled` before `onboarding.id` exists — see `isResumePending`. */
    workerId?: string | null;
  };
  onboarding: any;
  cache: any;
}

function hasWorkerData(onboarding: any): boolean {
  return !!(onboarding?.id || onboarding?.firstName || onboarding?.mobilePhoneNumber || onboarding?.email);
}

function getFirstIncompleteRoute(
  onboarding: any,
  profileFieldConstraintsMap: any,
  canCreateUnverifiedWorkers: boolean,
  isWorker: boolean,
  consentAgreements?: any[],
): string {
  const step = getFirstIncompleteStep({
    onboarding,
    profileFieldConstraintsMap,
    canCreateUnverifiedWorkers,
    consentAgreements,
  });
  if (step?.route) return step.route;
  // All steps complete — fallback to home
  return isWorker ? ROUTES.PROJECT_LIST.path : ROUTES.WORKER_STATUS.path;
}

function canUseVisitedStepForNavigation(stepKey: StepKey): boolean {
  return [
    StepKey.TAKE_PHOTO,
    StepKey.PERSONAL_INFORMATION,
    StepKey.CONTACT_INFORMATION,
    StepKey.TRADES,
    StepKey.CERTIFICATIONS,
    StepKey.CONSENT_FORM,
  ].includes(stepKey);
}

function isStepReachable(
  targetStepIndex: number,
  onboarding: any,
  profileFieldConstraintsMap: any,
  canCreateUnverifiedWorkers: boolean,
  visitedWizardSteps: StepKey[] = [],
  project: IProject,
  consentAgreements?: any[],
): boolean {
  for (let i = 0; i < targetStepIndex; i++) {
    const step = WIZARD_STEPS[i];

    if (step.shouldSkip) {
      const mockState = { cache: { profileFieldConstraintsMap, project }, onboarding } as any;
      if (step.shouldSkip(mockState)) continue;
    }

    const stepKey = step.key as StepKey;
    const isCompleted = isStepComplete({
      stepKey,
      onboarding,
      profileFieldConstraintsMap,
      canCreateUnverifiedWorkers,
      consentAgreements,
    });

    if (!isCompleted && !(canUseVisitedStepForNavigation(stepKey) && visitedWizardSteps.includes(stepKey))) {
      return false;
    }
  }
  return true;
}

export function getRedirectPath({ currentPath, user, onboarding, cache }: RouteGuardInput): string | null {
  const isLoggedIn = !!user?.userId;
  const isWorker = !!user?.isWorker;
  const isAdmin = isLoggedIn && !isWorker;

  const hasProjectAndEmployers = !!onboarding?.temp?.project && (onboarding?.temp?.employers?.length || 0) > 0;
  const hasWorker = hasWorkerData(onboarding);

  const isPasswordCreated = onboarding?.invitationStatus === WorkerInvitationStatus.ACCEPTED;
  const isUnverified =
    onboarding?.invitationStatus === WorkerInvitationStatus.NOT_INVITED ||
    (!onboarding?.email &&
      !onboarding?.mobilePhoneNumber &&
      (onboarding?.id || onboarding?.uploadingDetails?.pendingUploads?.workerCreation));
  const isWorkerFullyOnboarded = !!onboarding?.id && isPasswordCreated;
  // A worker who signed in with a one-time password but never set one is locked to the Create
  // Password tab (CH-4515).
  const mustCreatePassword = mustCreateWorkerPassword(onboarding);
  const isBiometricSigned = !!onboarding?.signedBiometricConsentForm;
  const hasEmailOrPhone = !!onboarding?.email || !!onboarding?.mobilePhoneNumber;
  const isRegistrationCompleted = hasEmailOrPhone || !!onboarding?.id;

  const profileFieldConstraintsMap = cache?.profileFieldConstraintsMap || {};
  const canCreateUnverifiedWorkers = cache?.canCreateUnverifiedWorkers || false;
  const visitedWizardSteps = onboarding?.temp?.visitedWizardSteps || [];
  const errorPageUrl = onboarding?.uploadingDetails?.errors?.pageUrl;

  const category = classifyRoute(currentPath);
  const path = currentPath.toLowerCase();

  // First consent sub-step the worker still needs to complete (null when all
  // standard agreements are accepted or none are loaded yet).
  const consentRoute = getFirstIncompleteConsentRoute(cache?.consentAgreements, onboarding);

  // Prevent redirect loops
  const safeRedirect = (target: string): string | null => {
    return target === path ? null : target;
  };

  // Allow access to the page where an upload error occurred
  if (errorPageUrl && path === errorPageUrl.toLowerCase()) {
    return null;
  }

  // ── SELECT PROJECT ─────────────────────────────────────────
  if (category === RouteCategory.SELECT_PROJECT) {
    return null;
  }

  // ── LOGIN ──────────────────────────────────────────────────
  if (category === RouteCategory.LOGIN) {
    if (!isLoggedIn) return null;
    // CH-4652: a worker who self-registered and has just authenticated stays put while `useLogin`
    // attaches them to the project picked before the duplicate check. Redirecting here would flip
    // `isRedirecting`, and AppRoot swaps the whole route tree for a spinner when it does — killing
    // the in-flight resume. It clears this marker and navigates itself, on success and on failure.
    if (isResumePending(onboarding, user)) return null;
    if (isWorker) return safeRedirect(ROUTES.PROJECT_LIST.path);
    return null; // admin → allow (worker login within admin session)
  }

  // ── FORGOT PASSWORD / RESET PASSWORD ───────────────────────
  if (category === RouteCategory.FORGOT_RESET_PASSWORD) {
    if (!isLoggedIn) return null;
    if (isWorker) return safeRedirect(ROUTES.PROJECT_LIST.path);
    return null; // admin → allow (forgot/reset password within admin session)
  }

  // ── AUTHENTICATED (pages requiring login, e.g., my-profile) ─
  if (category === RouteCategory.AUTHENTICATED) {
    if (isLoggedIn) return null;
    return safeRedirect(ROUTES.LOGIN.path);
  }

  // ── OTHER (components, image-picker, 404, etc.) ────────────
  if (category === RouteCategory.OTHER) {
    return null;
  }

  // ── OTP LOGIN WITH NO PASSWORD YET ─────────────────────────
  // Everything the worker is allowed to see has already returned above (My Profile, Select
  // Project, Login). Funnel the remaining worker-facing screens back to the Create Password
  // tab. Admin-only screens are handled further down and stay reachable for the admin.
  if (mustCreatePassword && category === RouteCategory.WORKER_ONLY) {
    return safeRedirect(`${ROUTES.MY_PROFILE.path}?tab=password`);
  }

  // ── WIZARD: Consent registration sub-steps ─────────────────
  // (Terms & Conditions, Standard Consent, Biometric, Signature). These are only
  // reachable once the password is created; the wizard's Next/Back manages the
  // ordering between them, so we simply allow access rather than pinning to one step.
  if (category === RouteCategory.WIZARD_CONSENT_REGISTRATION) {
    if (!isLoggedIn) {
      return hasProjectAndEmployers && hasEmailOrPhone ? safeRedirect(ROUTES.CREATE_PASSWORD.path) : ROUTES.LOGIN.path;
    }
    if (!isPasswordCreated && !isUnverified) {
      return safeRedirect(ROUTES.CREATE_PASSWORD.path);
    }
    // Accessible only until a signature exists — once signed, the consent flow is
    // done, so move on to the next incomplete wizard step.
    if (onboarding?.signatureUrl) {
      return safeRedirect(
        getFirstIncompleteRoute(
          onboarding,
          profileFieldConstraintsMap,
          canCreateUnverifiedWorkers,
          isWorker,
          cache?.consentAgreements,
        ),
      );
    }
    // Enforce order: the worker may be on the first incomplete consent step or any
    // earlier (already-accepted) one, but cannot skip ahead. e.g. cannot open the
    // Biometric step before accepting Terms.
    if (consentRoute) {
      const currentIndex = CONSENT_ROUTE_SEQUENCE.indexOf(path);
      const allowedIndex = CONSENT_ROUTE_SEQUENCE.indexOf(consentRoute);
      if (currentIndex > allowedIndex) {
        return safeRedirect(consentRoute);
      }
    }
    return null; // allow
  }

  // ── NO LOGGED-IN USER ──────────────────────────────────────
  if (!isLoggedIn) {
    if (category === RouteCategory.WIZARD_REGISTRATION) {
      if (path === ROUTES.BASIC_INFORMATION.path || path === ROUTES.REGISTRATION.path) {
        if (!hasProjectAndEmployers) return ROUTES.LOGIN.path;
        // If registration is completed and password not yet created, redirect to create-password
        if (isRegistrationCompleted && !isPasswordCreated && hasEmailOrPhone) {
          return safeRedirect(ROUTES.CREATE_PASSWORD.path);
        }
        return null;
      }
      if (path === ROUTES.CREATE_PASSWORD.path) {
        // Workers without email or phone cannot access create-password
        return hasProjectAndEmployers && hasEmailOrPhone ? null : ROUTES.LOGIN.path;
      }
    }
    return ROUTES.LOGIN.path;
  }

  // ── LOGGED-IN, NO PROJECT+EMPLOYERS ────────────────────────
  if (!hasProjectAndEmployers) {
    if (category === RouteCategory.WORKER_ONLY) {
      if (isWorker) return null;
      if (isAdmin && isWorkerFullyOnboarded) return null;
      return safeRedirect(ROUTES.SELECT_PROJECT.path);
    }

    if (category === RouteCategory.ADMIN_ONLY) {
      if (isWorker) return safeRedirect(ROUTES.PROJECT_LIST.path);
      return null; // admin → allow
    }

    // Allow staying on /consent-form while the post-submission success modal
    // is displayed — temp has been cleared but the worker has accepted the project.
    if (path === ROUTES.CONSENT_FORM.path && hasWorkerAcceptedTheProject(onboarding?.assignedCompanies)) {
      return null;
    }

    // All wizard steps → redirect
    if (isWorker) return safeRedirect(ROUTES.PROJECT_LIST.path);
    return safeRedirect(ROUTES.SELECT_PROJECT.path);
  }

  // ── LOGGED-IN, PROJECT+EMPLOYERS PRESENT ───────────────────

  // Worker-only pages
  if (category === RouteCategory.WORKER_ONLY) {
    if (isWorker) return null;
    if (isAdmin && isWorkerFullyOnboarded) return null;
    return safeRedirect(ROUTES.SELECT_PROJECT.path);
  }

  // Admin-only pages
  if (category === RouteCategory.ADMIN_ONLY) {
    if (isWorker) return safeRedirect(ROUTES.PROJECT_LIST.path);
    return null;
  }

  // ── CONSENT GATE ───────────────────────────────────────────
  // Once the password is created, the worker must complete the consent sub-steps
  // (Terms → Standard Consent → Biometric → Signature) before any later wizard step.
  // The consent routes themselves are classified as WIZARD_CONSENT_REGISTRATION and
  // returned above, so they are exempt from this gate.
  if (isPasswordCreated && consentRoute) {
    return safeRedirect(consentRoute);
  }

  // ── WIZARD: Basic Information / Registration / Create Password ──
  if (category === RouteCategory.WIZARD_REGISTRATION) {
    if (isWorker) {
      return safeRedirect(
        getFirstIncompleteRoute(
          onboarding,
          profileFieldConstraintsMap,
          canCreateUnverifiedWorkers,
          true,
          cache?.consentAgreements,
        ),
      );
    }

    if (isAdmin) {
      if (!isPasswordCreated) {
        if (path === ROUTES.BASIC_INFORMATION.path || path === ROUTES.REGISTRATION.path) {
          // If registration is completed, redirect to create-password (only if email/phone exists)
          if (isRegistrationCompleted && hasEmailOrPhone) {
            return safeRedirect(ROUTES.CREATE_PASSWORD.path);
          }
          // Unverified workers should move past registration (no visible fields for them)
          if (isUnverified) {
            return safeRedirect(
              getFirstIncompleteRoute(
                onboarding,
                profileFieldConstraintsMap,
                canCreateUnverifiedWorkers,
                false,
                cache?.consentAgreements,
              ),
            );
          }
          return null; // allowed
        }
        if (path === ROUTES.CREATE_PASSWORD.path) {
          // Unverified workers (no email/phone) skip create-password → go to next incomplete step
          if (!hasEmailOrPhone) {
            return safeRedirect(
              getFirstIncompleteRoute(
                onboarding,
                profileFieldConstraintsMap,
                canCreateUnverifiedWorkers,
                false,
                cache?.consentAgreements,
              ),
            );
          }
          return null;
        }
      } else {
        // Password created → none allowed, first incomplete step
        return safeRedirect(
          getFirstIncompleteRoute(
            onboarding,
            profileFieldConstraintsMap,
            canCreateUnverifiedWorkers,
            false,
            cache?.consentAgreements,
          ),
        );
      }
    }
  }

  // ── WIZARD: Take Photo, Personal Info, Contact Info, Trades,
  //           Certifications, Consent Form ────────────────────
  if (category === RouteCategory.WIZARD_STEP) {
    const checkStep = (): string | null => {
      const stepIndex = getStepIndexFromPath(currentPath);
      const currentStep = WIZARD_STEPS[stepIndex];

      // If current step should be skipped, redirect to first incomplete
      if (currentStep?.shouldSkip) {
        const mockState = { cache: { profileFieldConstraintsMap, project: cache.project }, onboarding } as any;
        if (currentStep.shouldSkip(mockState)) {
          return safeRedirect(
            getFirstIncompleteRoute(
              onboarding,
              profileFieldConstraintsMap,
              canCreateUnverifiedWorkers,
              isWorker,
              cache?.consentAgreements,
            ),
          );
        }
      }

      // Check if all previous steps are complete
      if (
        isStepReachable(
          stepIndex,
          onboarding,
          profileFieldConstraintsMap,
          canCreateUnverifiedWorkers,
          visitedWizardSteps,
          cache.project,
          cache?.consentAgreements,
        )
      ) {
        return null; // allow
      }
      return safeRedirect(
        getFirstIncompleteRoute(
          onboarding,
          profileFieldConstraintsMap,
          canCreateUnverifiedWorkers,
          isWorker,
          cache?.consentAgreements,
        ),
      );
    };

    if (isWorker) return checkStep();
    if (isAdmin) {
      if (hasWorker) return checkStep();
      return safeRedirect(ROUTES.SELECT_PROJECT.path);
    }
  }

  // ── Consent Form Information ───────────────────────────────
  if (category === RouteCategory.CONSENT_FORM_INFO) {
    const checkConsentFormInfo = (): string | null => {
      if (hasWorkerAcceptedTheProject(onboarding?.assignedCompanies)) {
        return null; // allow
      }
      return safeRedirect(
        getFirstIncompleteRoute(
          onboarding,
          profileFieldConstraintsMap,
          canCreateUnverifiedWorkers,
          isWorker,
          cache?.consentAgreements,
        ),
      );
    };

    if (isWorker) return checkConsentFormInfo();
    if (isAdmin) {
      if (hasWorker) return checkConsentFormInfo();
      return safeRedirect(ROUTES.SELECT_PROJECT.path);
    }
  }

  return null;
}
